import { z } from 'zod';

/**
 * The furthest from the start an item can sit, in minutes.
 *
 * Exported because the client both clamps against it — drags and clicks, which
 * stop here rather than being refused — and validates against it, in the
 * template form, where the planner types an offset and is told it is too far.
 * Either way a client number that is not this one is a gesture the UI accepts
 * and the API refuses, or a time the UI refuses and the API would have taken.
 *
 * It was written out three times in the frontend: twice as a drag clamp, and
 * once as the template form's own ceiling. Widening the bound here left all
 * three a day behind it, and the third was the one that mattered, because a
 * ceiling refuses where a clamp quietly absorbs: it would have answered "no
 * more than 24 hr either side" to an offset this schema stores.
 */
export const MAX_OFFSET_MINUTES = 2880;

/**
 * A drag or a click, as early or as late as the schema will take and no
 * further.
 *
 * Deliberately not the axis's own edges. Those used to be derived from the
 * earliest item, so clamping a drag to them made the first block of the day
 * unable to move earlier than itself — the floor moved down with it and there
 * was no way back. The axis grows to fit instead, which is what it is for.
 */
export function clampOffsetMinutes(offsetMinutes: number): number {
  return Math.min(MAX_OFFSET_MINUTES, Math.max(-MAX_OFFSET_MINUTES, offsetMinutes));
}

/**
 * Two days either side of the start, which is what the axis can draw.
 *
 * It was one, on the reasoning that a day either side is already far past
 * anything a real wedding reaches — but the axis now covers whole calendar
 * days and grows by a whole one when the night runs past midnight, so an
 * ordinary 17:30-02:30 reception reaches +1830. A bound inside what the axis
 * draws is worse than a tight bound: the planner clicks a time, watches the
 * draft preview it, and gets an error.
 *
 * `dayOffset` in `lib/timeline.ts` therefore spans five values rather than
 * three, and `dayText` in `TimelineBlock` has wording for all of them.
 */
const offsetMinutes = z
  .number()
  .int()
  .min(-MAX_OFFSET_MINUTES, 'That is more than two days before the wedding starts')
  .max(MAX_OFFSET_MINUTES, 'That is more than two days after the wedding starts');

const DURATION_NEGATIVE_MESSAGE = 'A duration cannot be negative';
const DURATION_TOO_LONG_MESSAGE = 'An item cannot run longer than a day';

/**
 * How long a single item may run, in minutes.
 *
 * One number for both duration fields below. `templateItemFields` restates the
 * field to collapse its three presence states into two, not to bound it
 * differently — the length an item may run is the same question whether it is
 * being PATCHed onto a wedding or stored in a template, and two `1440`s for one
 * question is the drift `MAX_OFFSET_MINUTES` above was fixed for.
 *
 * Exported for the same reason that one is: `HoursMinutesField` refuses a typed
 * length before the request, and a client ceiling that is not this number is a
 * form disagreeing with the server about what an item can be.
 *
 * Not to be folded into `DAY_MINUTES` in `frontend/src/lib/timeline-geometry.ts`
 * despite both being 1440. That one is a unit — how many minutes a day has, in
 * arithmetic that converts offsets to pixels — and this is a policy about how
 * long one item may run. Unifying them would make a decision to allow
 * day-and-a-half items move the axis.
 */
export const MAX_DURATION_MINUTES = 1440;

/**
 * Null is meaningful: the item runs to the next one, or to the end of the
 * night. `.optional()` on top of that is what lets a PATCH distinguish "leave
 * the duration alone" (the key absent) from "make it open-ended" (the key
 * sent as null) — see `updateTimelineItemSchema` below. A stored template has
 * no "leave it alone" case, so `templateItemFields` below replaces this with
 * a required field instead of reusing it.
 */
const durationMinutes = z
  .number()
  .int()
  .min(0, DURATION_NEGATIVE_MESSAGE)
  .max(MAX_DURATION_MINUTES, DURATION_TOO_LONG_MESSAGE)
  .nullable()
  .optional();

export const timelineItemFields = z.object({
  label: z.string().trim().min(1, 'Give this part of the day a name').max(120),
  offsetMinutes,
  durationMinutes,
  note: z.string().trim().max(500).nullable().optional(),
});

export const createTimelineItemSchema = timelineItemFields;

/**
 * Whether a PATCH actually asks for something.
 *
 * `Object.values`, not `Object.keys`: a key Zod never saw in the input is
 * dropped from the output rather than materialized as `undefined`, so in
 * practice every key present here carries a real value — `null` included,
 * since `null !== undefined`. `Object.values` stays correct even in the one
 * case `Object.keys` would not: a key sent with the literal value
 * `undefined`, which JSON itself cannot express but which a caller
 * constructing the object in code could still produce.
 *
 * Exported so a route-layer schema that composes `timelineItemFields`
 * differently (adding `clockTime`, say) can apply the same rule rather than
 * re-deriving it.
 */
export const timelineItemHasChanges = (v: object) =>
  Object.values(v).some((value) => value !== undefined);
export const timelineItemHasChangesMessage = 'Nothing to change';

/** Every field optional, but at least one present — an empty PATCH is a bug, not a no-op. */
export const updateTimelineItemSchema = timelineItemFields
  .partial()
  .refine(timelineItemHasChanges, timelineItemHasChangesMessage);

/**
 * A stored template item's duration, collapsed to a single open-ended state.
 *
 * A PATCH has three states to distinguish — absent, null, a number — because
 * "leave it alone" is a real request against an item that already exists. A
 * template read back whole from jsonb has no such history to preserve: every
 * item either has a duration or is open-ended, full stop. Collapsing an
 * absent duration into `null` here, at the parse boundary, is what lets
 * `generateTimeline` and `resolveTimeline` in `lib/timeline.ts` — both typed
 * against `durationMinutes: number | null`, with no third state — take a
 * parsed template directly, with no cast or `?? null` at every call site.
 */
export const templateItemFields = timelineItemFields.extend({
  durationMinutes: z
    .number()
    .int()
    .min(0, DURATION_NEGATIVE_MESSAGE)
    .max(MAX_DURATION_MINUTES, DURATION_TOO_LONG_MESSAGE)
    .nullable()
    .default(null),
});

/**
 * The planner's template, stored whole as jsonb on `user`.
 *
 * Non-empty because an empty template would seed every new wedding with a
 * blank timeline while reporting success. A planner who wants no timeline
 * deletes the rows on the wedding; the template is not where that is said.
 */
export const timelineTemplateSchema = z.object({
  items: z
    .array(templateItemFields)
    .min(1, 'A template needs at least one item')
    .max(60, 'A template can hold at most 60 items'),
});

export type CreateTimelineItem = z.infer<typeof createTimelineItemSchema>;
export type UpdateTimelineItem = z.infer<typeof updateTimelineItemSchema>;
export type TimelineTemplate = z.infer<typeof timelineTemplateSchema>;
